import React, { useState, useEffect } from 'react';
import { FiHome, FiUsers, FiBook, FiPlus, FiEdit, FiTrash2, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { teacherSubjectAPI } from '../../api/teacherEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  SectionTitle,
  ContentGrid,
  EmptyState,
  FilterBar,
  SearchInput,
} from '../dashboards/Dashboard.styles';

const TeacherSubjects = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjectToDelete, setSubjectToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
  });

  const menuItems = [
    { icon: <FiHome />, label: 'Dashboard', path: '/teacher' },
    { icon: <FiUsers />, label: 'Grades', path: '/teacher/grades' },
    { icon: <FiBook />, label: 'Subjects', path: '/teacher/subjects' },
  ];

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    filterSubjects();
  }, [searchQuery, subjects]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const response = await teacherSubjectAPI.getSubjects();
      setSubjects(response.data.subjects || []);
    } catch (error) {
      showToast('error', 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  const filterSubjects = () => {
    let filtered = subjects;
    if (searchQuery) {
      filtered = filtered.filter(subject =>
        subject.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (subject.code && subject.code.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    setFilteredSubjects(filtered);
  };

  const handleCreateSubject = async () => {
    if (!formData.name.trim()) {
      showToast('error', 'Subject name is required');
      return;
    }

    try {
      await teacherSubjectAPI.createSubject(formData);
      showToast('success', 'Subject created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', code: '', description: '' });
      fetchSubjects();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to create subject');
    }
  };

  const handleEditSubject = async () => {
    if (!formData.name.trim()) {
      showToast('error', 'Subject name is required');
      return;
    }

    try {
      await teacherSubjectAPI.updateSubject(selectedSubject.id, formData);
      showToast('success', 'Subject updated successfully');
      setShowEditModal(false);
      setSelectedSubject(null);
      setFormData({ name: '', code: '', description: '' });
      fetchSubjects();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to update subject');
    }
  };

  const handleDeleteSubject = async () => {
    try {
      await teacherSubjectAPI.deleteSubject(subjectToDelete.id);
      showToast('success', 'Subject deleted successfully');
      setShowDeleteModal(false);
      setSubjectToDelete(null);
      fetchSubjects();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to delete subject');
    }
  };

  const openEditModal = (subject) => {
    setSelectedSubject(subject);
    setFormData({
      name: subject.name,
      code: subject.code || '',
      description: subject.description || '',
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (subject) => {
    setSubjectToDelete(subject);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', code: '', description: '' });
    setSelectedSubject(null);
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading subjects..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Manage Subjects 📚</h1>
            <p>Create and manage curriculum subjects</p>
          </div>
          <Button 
            leftIcon={<FiPlus />} 
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
          >
            Add Subject
          </Button>
        </DashboardHeader>

        {/* Search */}
        <FilterBar>
          <SearchInput
            placeholder="Search subjects by name or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </FilterBar>

        {/* Subjects List */}
        {filteredSubjects.length === 0 ? (
          <EmptyState>
            <FiBook size={48} />
            <h3>No subjects found</h3>
            <p>
              {subjects.length === 0
                ? "You haven't created any subjects yet. Click 'Add Subject' to get started."
                : "No subjects match your search criteria."
              }
            </p>
            {subjects.length === 0 && (
              <Button 
                leftIcon={<FiPlus />} 
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
              >
                Add Subject
              </Button>
            )}
          </EmptyState>
        ) : (
          <ContentGrid>
            {filteredSubjects.map((subject) => (
              <div
                key={subject.id}
                style={{
                  background: theme.colors.surface,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '12px',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{ flex: 1 }}>
                  <h3 style={{ 
                    margin: '0 0 8px 0', 
                    color: theme.colors.textPrimary,
                    fontSize: '20px',
                    fontWeight: '600'
                  }}>
                    {subject.name}
                  </h3>
                  {subject.code && (
                    <p style={{ 
                      margin: '0 0 8px 0', 
                      color: theme.colors.textSecondary,
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      Code: {subject.code}
                    </p>
                  )}
                  {subject.description && (
                    <p style={{ 
                      margin: '8px 0 0 0', 
                      color: theme.colors.textTertiary,
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}>
                      {subject.description}
                    </p>
                  )}
                </div>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginTop: '8px',
                  borderTop: `1px solid ${theme.colors.border}`,
                  paddingTop: '16px'
                }}>
                  <Button 
                    variant="ghost" 
                    size="small"
                    leftIcon={<FiEdit />}
                    onClick={() => openEditModal(subject)}
                    style={{ flex: 1 }}
                  >
                    Edit
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="small"
                    leftIcon={<FiTrash2 />}
                    onClick={() => openDeleteModal(subject)}
                    style={{ 
                      flex: 1,
                      color: '#EF4444',
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </ContentGrid>
        )}

        {/* Create Subject Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          title="Create New Subject"
          size="medium"
        >
          <div style={{ padding: '20px 0' }}>
            <Input
              label="Subject Name *"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Mathematics"
            />
            <Input
              label="Subject Code (Optional)"
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder="e.g., MATH"
            />
            <Input
              label="Description (Optional)"
              type="textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the subject"
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <Button 
                variant="ghost" 
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateSubject}>
                Create Subject
              </Button>
            </div>
          </div>
        </Modal>

        {/* Edit Subject Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            resetForm();
          }}
          title="Edit Subject"
          size="medium"
        >
          <div style={{ padding: '20px 0' }}>
            <Input
              label="Subject Name *"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Mathematics"
            />
            <Input
              label="Subject Code (Optional)"
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder="e.g., MATH"
            />
            <Input
              label="Description (Optional)"
              type="textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the subject"
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <Button 
                variant="ghost" 
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleEditSubject}>
                Update Subject
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSubjectToDelete(null);
          }}
          title="Delete Subject"
          size="small"
        >
          <div style={{ padding: '20px 0' }}>
            <p style={{ margin: '0 0 20px 0', color: theme.colors.textSecondary }}>
              Are you sure you want to delete this subject? This action cannot be undone.
            </p>
            {subjectToDelete && (
              <div style={{ 
                background: theme.colors.backgroundSecondary, 
                padding: '12px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: `1px solid ${theme.colors.border}`
              }}>
                <p style={{ margin: 0, fontWeight: '600', color: theme.colors.textPrimary }}>
                  {subjectToDelete.name}
                </p>
                {subjectToDelete.code && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: theme.colors.textSecondary }}>
                    Code: {subjectToDelete.code}
                  </p>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button 
                variant="ghost" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setSubjectToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleDeleteSubject}
                style={{ background: '#EF4444' }}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default TeacherSubjects;

